import { useTranslation } from "react-i18next"

import type { HostedAgentEvalCaseDraft } from "../utils/hosted-agent-eval-cases"
import type {
  SkillPackageBudgets,
  SkillPackageFormState,
  SkillPackageFormValidation,
} from "../utils/skill-package-draft"

import { Plus, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"

import { HostedAgentEvalCaseEditor } from "./hosted-agent-eval-case-editor"
import { SkillPackageBudgetCounter, SkillPackageFormError } from "./skill-package-editor-feedback"
import { validateHostedAgentEvalCase } from "../utils/hosted-agent-eval-cases"
import { createBlankSkillPackageEvalCase } from "../utils/skill-package-draft"

// The "Eval cases" tab (#3074).
//
// It reuses the agent wizard's own case editor rather than growing a second
// one: a package's cases are seeded verbatim onto an agent at install time, so
// anything the two editors disagreed about would become a case that validates
// here and is refused there.

export function SkillPackageEvalsTab({
  form,
  patch,
  budgets,
  validation,
  showErrors,
}: {
  form: SkillPackageFormState
  patch: (partial: Partial<SkillPackageFormState>) => void
  budgets: SkillPackageBudgets
  validation: SkillPackageFormValidation
  showErrors: boolean
}) {
  const { t } = useTranslation()

  const updateCase = (index: number, changes: Partial<HostedAgentEvalCaseDraft>) => {
    patch({
      evalCases: form.evalCases.map((entry, position) =>
        position === index ? { ...entry, ...changes } : entry
      ),
    })
  }

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.skillPackages.editor.evalsHelp", {
          defaultValue:
            "Tests that travel with the skill. Installing the package seeds them onto the agent, so the specialization is measurable from day one.",
        })}
      </p>
      <SkillPackageBudgetCounter budget={budgets.evalCases} unit="cases" />

      <ul className="space-y-3">
        {form.evalCases.map((evalCase, index) => (
          <li key={evalCase.id ?? index} className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">
                {evalCase.name.trim() ||
                  t("adminDashboard.skillPackages.editor.untitledCase", {
                    defaultValue: "Untitled case",
                  })}
              </span>
              <Button
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.skillPackages.editor.removeCase", {
                  defaultValue: "Remove eval case",
                })}
                onClick={() =>
                  patch({
                    evalCases: form.evalCases.filter((_, position) => position !== index),
                  })
                }
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
            <HostedAgentEvalCaseEditor
              draft={evalCase}
              showErrors={showErrors}
              validation={validateHostedAgentEvalCase(evalCase)}
              onChange={(changes) => updateCase(index, changes)}
            />
          </li>
        ))}
      </ul>

      <Button
        data-testid="skill-package-add-eval-case"
        disabled={budgets.evalCases.used >= budgets.evalCases.max}
        size="sm"
        type="button"
        variant="outline"
        onClick={() =>
          patch({
            evalCases: [...form.evalCases, createBlankSkillPackageEvalCase(form.evalCases.length)],
          })
        }
      >
        <Plus className="size-4" />
        {t("adminDashboard.skillPackages.editor.addCase", { defaultValue: "Add eval case" })}
      </Button>
      <SkillPackageFormError
        codes={validation.form}
        match={["too_many_eval_cases", "eval_case_invalid"]}
        show={showErrors}
      />
    </div>
  )
}
